import { Alert, AlertIcon, Box, Button, Heading, Icon, Link, Spinner, Stack, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaFilePdf } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import {
  PreflightRequirementDto,
  PreflightWarningDto,
  PublicBidStatus,
  useGetPublicBidQuery,
  useGetPublicBidSigningPreflightQuery,
  useStartPublicBidSigningMutation,
  type PortalSourcingEventDetailDto,
  type PublicBidDto,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { getOrganizationId } from "../../../../../common/local-storage/selected-organization";
import { PreviewPdfModal } from "../../../../../common/documents/PreviewPdfModal";
import { getPusher } from "../../../../../common/pusher";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { urls } from "../../../../../urls";
import { EventDeadlineHighlight } from "../../view-single/EventDeadlineHighlight";
import { useBidStepNavigation } from "../useBidStepNavigation";

interface Props {
  eventId: string;
  bidId: string;
  bid: PublicBidDto;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
}

export const ReviewStep = ({ eventId, bidId, bid, event, previousStep }: Props) => {
  const { t } = useTranslation();
  const apiError = useApiError();
  const [searchParams] = useSearchParams();
  const collectSupplierPrice = event.collectSupplierPrice ?? true;
  const { navigateToStep } = useBidStepNavigation(eventId, bidId, collectSupplierPrice);
  const signingOutcome = searchParams.get("signering");

  const isSubmitted = bid.status === PublicBidStatus.Submitted;

  const { data: preflight } = useGetPublicBidSigningPreflightQuery({ bidId }, { skip: isSubmitted });
  const [startSigning, { isLoading: isStarting }] = useStartPublicBidSigningMutation();
  const { refetch: refetchBid } = useGetPublicBidQuery({ bidId });

  const [previewUrl, setPreviewUrl] = useState<string>();
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [tookTooLong, setTookTooLong] = useState(false);

  // After Signicat redirects the supplier back, the BFF's signing webhook finalizes delivery and
  // emits a "bid-delivered" Pusher event on this bid's channel. We subscribe here so the page
  // flips to the submitted confirmation as soon as the bid lands, without polling.
  useEffect(() => {
    if (signingOutcome !== "fullfort" || isSubmitted) return;
    const channel = getPusher().subscribe(bidId);
    const onDelivered = () => {
      refetchBid();
    };
    channel.bind("bid-delivered", onDelivered);
    // Cover the race where the webhook finalized the bid before this effect bound the handler —
    // Pusher doesn't buffer public-channel events, so without this the spinner would sit until
    // the 30s warning. The handler is idempotent (RTK refetch).
    refetchBid();
    const timer = window.setTimeout(() => setTookTooLong(true), 30_000);
    return () => {
      channel.unbind("bid-delivered", onDelivered);
      window.clearTimeout(timer);
    };
  }, [signingOutcome, isSubmitted, bidId, refetchBid]);

  // Object URLs hold the PDF bytes alive until revoked. This cleanup runs whenever previewUrl
  // changes (releasing the old one) and on unmount, so no URL is ever leaked.
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const openPreview = async () => {
    setIsPreviewLoading(true);
    try {
      const organizationId = getOrganizationId();
      const response = await fetch(`${import.meta.env.VITE_BFF_BASE_URL}/api/portal/bids/${bidId}/report`, {
        credentials: "include",
        headers: organizationId ? { organizationId } : {},
      });
      if (!response.ok) throw new Error(`Report preview failed with status ${response.status}`);
      const blob = await response.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      apiError.trigger(e);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSignAndDeliver = async () => {
    const path = urls.portal.bid.edit.go(eventId, bidId).review;
    const base = `${window.location.origin}${path.pathname}?${path.search}`;
    try {
      const { signingUrl } = await startSigning({
        bidId,
        startPublicBidSigningRequestDto: {
          successUrl: `${base}&signering=fullfort`,
          cancelUrl: `${base}&signering=avbrutt`,
          errorUrl: `${base}&signering=feilet`,
        },
      }).unwrap();
      window.location.href = signingUrl;
    } catch (e) {
      apiError.trigger(e);
    }
  };

  if (isSubmitted) {
    const submittedTime = bid.submittedAt
      ? new Date(bid.submittedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
      : null;
    return (
      <Box pb="8" pt="10">
        <Stack
          spacing="4"
          align="center"
          textAlign="center"
          borderWidth="1px"
          borderColor="smBorder"
          borderRadius="lg"
          py="16"
          px="8"
        >
          <Icon as={FaCheckCircle} boxSize="10" color="smPrimary" />
          <Heading size="md" color="smPrimary">
            {submittedTime
              ? t("The offer was delivered at {{time}}.", { time: submittedTime })
              : t("The offer has been submitted")}
          </Heading>
          <Text fontSize="sm" color="smMuted">
            {t("You will receive an email receipt shortly.")}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t("Reference: {{reference}}", { reference: bid.id })}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t("The offer is now locked and can no longer be edited.")}
          </Text>
          <Stack spacing="1" align="center">
            <Text fontWeight="semibold" color="smMuted" textDecoration="underline" cursor="not-allowed">
              {t("See the delivered offer")}
            </Text>
            <Text fontSize="xs" color="smMuted">
              {t("Detailed view of the delivered offer is coming in a later update.")}
            </Text>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (signingOutcome === "fullfort") {
    return (
      <Box pb="8" pt="10">
        <Stack spacing="4" align="center" textAlign="center" py="16">
          <Spinner color="smPrimary" />
          <Text color="smMuted">{t("Signing complete — confirming delivery…")}</Text>
          {tookTooLong && (
            <Alert status="warning" borderRadius="lg" maxW="md">
              <AlertIcon />
              <Text fontSize="sm">
                {t("This is taking longer than usual. Reload the page if it doesn't resolve.")}
              </Text>
            </Alert>
          )}
        </Stack>
      </Box>
    );
  }

  const remaining = preflight?.remaining ?? [];
  const warnings = preflight?.warnings ?? [];
  const isReady = preflight?.ready ?? false;

  return (
    <Box pb="8" pt="6">
      <Stack spacing="2" mb="6">
        <Heading size="md">{t("Summary and submission")}</Heading>
        <Text fontSize="sm" color="smMuted">
          {t("Preview the report and sign your offer with BankID.")}
        </Text>
      </Stack>

      <Stack spacing="6" mb="8">
        <EventDeadlineHighlight event={event} />

        {signingOutcome === "avbrutt" && (
          <Alert status="warning" borderRadius="lg">
            <AlertIcon />
            <Text fontSize="sm">{t("Signing was cancelled. You can try again.")}</Text>
          </Alert>
        )}
        {signingOutcome === "feilet" && (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Text fontSize="sm">{t("Signing failed. You can try again.")}</Text>
          </Alert>
        )}

        {remaining.length > 0 && (
          <Alert status="warning" borderRadius="lg" alignItems="flex-start">
            <AlertIcon />
            <Stack spacing="2">
              <Text fontSize="sm" fontWeight="semibold">
                {t("Complete the following before you can deliver:")}
              </Text>
              <Stack spacing="1">
                {remaining.map((requirement) => (
                  <RemainingRequirement
                    key={requirement}
                    requirement={requirement}
                    onJumpToSupplier={() => navigateToStep("supplier")}
                  />
                ))}
              </Stack>
            </Stack>
          </Alert>
        )}

        {warnings.includes(PreflightWarningDto.PriceMissing) && (
          <Alert status="warning" borderRadius="lg" alignItems="flex-start">
            <AlertIcon />
            <Text fontSize="sm">
              {t("No total price entered. The buyer may be unable to score your offer's price.")}{" "}
              <Link color="smPrimary" onClick={() => navigateToStep("price")}>
                {t("Go to price")}
              </Link>
            </Text>
          </Alert>
        )}

        <Button
          leftIcon={<FaFilePdf />}
          variant="outline"
          alignSelf="flex-start"
          onClick={openPreview}
          isLoading={isPreviewLoading}
        >
          {t("Preview report")}
        </Button>
      </Stack>

      <StepNavigation
        onPrevious={previousStep}
        onNext={handleSignAndDeliver}
        nextLabel={t("Sign and deliver")}
        nextIsAction
        isNextLoading={isStarting}
        isNextDisabled={!isReady}
      />

      {previewUrl && <PreviewPdfModal url={previewUrl} onClose={() => setPreviewUrl(undefined)} />}
    </Box>
  );
};

const RemainingRequirement = ({
  requirement,
  onJumpToSupplier,
}: {
  requirement: PreflightRequirementDto;
  onJumpToSupplier: () => void;
}) => {
  const { t } = useTranslation();
  switch (requirement) {
    case PreflightRequirementDto.SupplierInfoIncomplete:
      return (
        <Text fontSize="sm">
          {t("All self-declaration questions must be answered.")}{" "}
          <Link color="smPrimary" onClick={onJumpToSupplier}>
            {t("Go to self-declaration")}
          </Link>
        </Text>
      );
    case PreflightRequirementDto.DeadlinePassed:
      return <Text fontSize="sm">{t("The submission deadline has passed.")}</Text>;
    case PreflightRequirementDto.NotDraft:
      return <Text fontSize="sm">{t("This offer has already been submitted.")}</Text>;
    default:
      return null;
  }
};
